import { db, type LiftXDatabase } from '../db';
import { isLive, tombstoneById, tombstoneRows } from '../tombstone';
import type { UserMediaRecord } from '../types';

/**
 * The only code that reads or writes the `userMedia` table.
 *
 * Every write here also touches the owning `ExerciseRecord`'s `mediaIds` and
 * `thumbnailMediaId` — the two must never drift apart, so every method that
 * changes one changes the other in the **same** Dexie transaction (Phase 4B
 * §9/§6).
 *
 * `thumbnailMediaId` is derived, never independently chosen: it is always
 * `mediaIds[0]` (or `null` for an empty gallery). Position 1 is the primary
 * image, full stop — there is no separate "pick a cover regardless of order"
 * affordance (Exercise Images feature, 2026-09-14). Reordering is the only
 * way to change which item is primary.
 *
 * User media only. Catalogue GIFs live in Cache Storage and are never touched
 * by this file (Phase 0 §8.2, reaffirmed for Phase 4B).
 */

/** Up to 3 images per exercise (Exercise Images feature). Videos are not capped. */
export const MAX_IMAGES_PER_EXERCISE = 3;

export interface AddMediaInput {
  exerciseId: string;
  kind: 'image' | 'video';
  blob: Blob;
  thumbnailBlob: Blob | null;
  mimeType: string;
  byteCount: number;
  width: number | null;
  height: number | null;
  durationS: number | null;
  caption: string | null;
}

/** userMedia rows whose owning exercise no longer exists — deleted by the sweep. */
export interface OwnerlessMedia {
  id: string;
  exerciseId: string;
}

/** An exercise pointing at a userMedia id that no longer exists — reported only. */
export interface MissingMediaReference {
  exerciseId: string;
  missingMediaId: string;
  /** Whether the dangling id was the gallery entry or the chosen cover. */
  role: 'gallery' | 'thumbnail';
}

export interface OrphanSweepResult {
  ownerlessMediaDeleted: OwnerlessMedia[];
  missingReferences: MissingMediaReference[];
}

export interface MediaRepository {
  listForExercise(exerciseId: string): Promise<UserMediaRecord[]>;
  /**
   * A single media row by id, regardless of live/tombstoned state — used to
   * resolve a frozen `SessionExerciseRecord.mediaIdsSnapshot` id. Unlike
   * `listForExercise`, this deliberately does NOT filter by `isLive`: a
   * workout report must be able to keep showing the exact image that was
   * primary when the workout was recorded even if that image has since been
   * removed from the exercise's current gallery (Exercise Images feature).
   */
  getById(mediaId: string): Promise<UserMediaRecord | undefined>;
  add(input: AddMediaInput): Promise<UserMediaRecord>;
  remove(mediaId: string): Promise<void>;
  /** Also updates `thumbnailMediaId` to `orderedMediaIds[0]` — position 1 is always primary. */
  reorder(exerciseId: string, orderedMediaIds: string[]): Promise<void>;
  /**
   * Ownerless rows (no owning exercise) are deleted automatically — Phase 0
   * §8.4. Exercises referencing a missing media id are only ever reported —
   * deleting the reference would be guessing what was lost.
   */
  sweepOrphans(): Promise<OrphanSweepResult>;
}

export function createMediaRepository(database: LiftXDatabase = db): MediaRepository {
  return {
    async listForExercise(exerciseId: string): Promise<UserMediaRecord[]> {
      const rows = await database.userMedia.where('exerciseId').equals(exerciseId).filter(isLive).toArray();
      return rows.sort((a, b) => a.orderIndex - b.orderIndex);
    },

    async getById(mediaId: string): Promise<UserMediaRecord | undefined> {
      return await database.userMedia.get(mediaId);
    },

    /**
     * Appends to the gallery. The new row's `orderIndex` is one past the
     * current maximum, so imports land at the end rather than reshuffling
     * existing media. `thumbnailMediaId` is re-derived as `mediaIds[0]` on
     * every write — for an append this only changes anything the first time
     * (an empty gallery gaining its first item), matching the prior
     * "first item becomes cover automatically" behaviour.
     *
     * Images are capped at {@link MAX_IMAGES_PER_EXERCISE}; videos are not.
     */
    async add(input: AddMediaInput): Promise<UserMediaRecord> {
      return await database.transaction(
        'rw',
        [database.userMedia, database.exercises],
        async () => {
          const exercise = await database.exercises.get(input.exerciseId);
          if (exercise === undefined) {
            throw new Error(`Cannot add media: exercise ${input.exerciseId} does not exist`);
          }

          const existing = await database.userMedia
            .where('exerciseId')
            .equals(input.exerciseId)
            .filter(isLive)
            .toArray();

          if (input.kind === 'image') {
            const imageCount = existing.filter((row) => row.kind === 'image').length;
            if (imageCount >= MAX_IMAGES_PER_EXERCISE) {
              throw new Error(`An exercise can have at most ${String(MAX_IMAGES_PER_EXERCISE)} images.`);
            }
          }

          const nextOrder = existing.reduce((max, row) => Math.max(max, row.orderIndex), -1) + 1;

          const record: UserMediaRecord = {
            id: crypto.randomUUID(),
            exerciseId: input.exerciseId,
            kind: input.kind,
            blob: input.blob,
            thumbnailBlob: input.thumbnailBlob,
            mimeType: input.mimeType,
            byteCount: input.byteCount,
            width: input.width,
            height: input.height,
            durationS: input.durationS,
            orderIndex: nextOrder,
            caption: input.caption,
            createdAt: Date.now(),
            // A fresh blob that hasn't reached liftx-media yet — the sync
            // engine's upload step scans for exactly this.
            uploadStatus: 'pending',
          };

          await database.userMedia.add(record);

          const mediaIds = [...exercise.mediaIds, record.id];
          const thumbnailMediaId = mediaIds[0] ?? null;
          await database.exercises.update(input.exerciseId, {
            mediaIds,
            thumbnailMediaId,
            updatedAt: Date.now(),
          });

          return record;
        },
      );
    },

    /**
     * Deletes one media row and removes it from its exercise's `mediaIds` in
     * the same transaction — the specific inconsistency this design guards
     * against is a `mediaIds` entry surviving its blob's deletion.
     *
     * `thumbnailMediaId` is re-derived as `mediaIds[0]` after the removal —
     * so it falls back to the next remaining item, or `null` if the gallery
     * is now empty — never left pointing at the id that was just deleted.
     */
    async remove(mediaId: string): Promise<void> {
      await database.transaction(
        'rw',
        [database.userMedia, database.exercises],
        async () => {
          const media = await database.userMedia.get(mediaId);
          if (media === undefined || !isLive(media)) return; // Already gone — nothing to reconcile.

          await tombstoneById(database.userMedia, mediaId, Date.now());

          const exercise = await database.exercises.get(media.exerciseId);
          if (exercise === undefined) return; // Exercise deletion already cascaded this.

          const mediaIds = exercise.mediaIds.filter((id) => id !== mediaId);

          await database.exercises.update(media.exerciseId, {
            mediaIds,
            thumbnailMediaId: mediaIds[0] ?? null,
            updatedAt: Date.now(),
          });
        },
      );
    },

    /**
     * Rewrites `orderIndex` to match `orderedMediaIds` exactly, and sets
     * `thumbnailMediaId` to `orderedMediaIds[0]` — position 1 is always
     * primary, so reordering is the only way to change which item is
     * primary (Exercise Images feature). Blob, thumbnail and every other
     * field on each row are otherwise untouched, which is the specific
     * guarantee Phase 4B's data-preservation requirement #5 asks for.
     */
    async reorder(exerciseId: string, orderedMediaIds: string[]): Promise<void> {
      await database.transaction(
        'rw',
        [database.userMedia, database.exercises],
        async () => {
          const existing = await database.userMedia.where('exerciseId').equals(exerciseId).filter(isLive).toArray();
          const existingIds = new Set(existing.map((row) => row.id));

          if (
            orderedMediaIds.length !== existing.length ||
            !orderedMediaIds.every((id) => existingIds.has(id))
          ) {
            throw new Error('reorder: the id list must be exactly this exercise’s current media, reordered');
          }

          await Promise.all(
            orderedMediaIds.map((id, index) => database.userMedia.update(id, { orderIndex: index })),
          );

          await database.exercises.update(exerciseId, {
            mediaIds: orderedMediaIds,
            thumbnailMediaId: orderedMediaIds[0] ?? null,
            updatedAt: Date.now(),
          });
        },
      );
    },

    async sweepOrphans(): Promise<OrphanSweepResult> {
      const [allMedia, allExercises] = await Promise.all([
        database.userMedia.filter(isLive).toArray(),
        database.exercises.filter(isLive).toArray(),
      ]);
      const exerciseIds = new Set(allExercises.map((row) => row.id));

      // Direction 1: userMedia with no owning exercise. Auto-deleted — Phase 0
      // §8.4 states this explicitly, distinct from the general "report, never
      // delete" rule elsewhere in the app. In normal operation this list is
      // always empty, because `customExerciseRepository.deleteCustom` already
      // cascades `userMedia` in the same transaction; this exists as a safety
      // net against a bug or external corruption, not the primary deletion path.
      const ownerless = allMedia.filter((row) => !exerciseIds.has(row.exerciseId));

      if (ownerless.length > 0) {
        await tombstoneRows(database.userMedia, ownerless, Date.now());
      }

      // Direction 2: an exercise pointing at a media id that no longer exists.
      // Reported only — the row is the evidence something was lost, and a
      // guess at "what it probably was" would be worse than admitting it.
      const mediaIds = new Set(allMedia.map((row) => row.id));
      const missingReferences: MissingMediaReference[] = [];

      for (const exercise of allExercises) {
        for (const mediaId of exercise.mediaIds) {
          if (!mediaIds.has(mediaId)) {
            missingReferences.push({ exerciseId: exercise.id, missingMediaId: mediaId, role: 'gallery' });
          }
        }
        if (exercise.thumbnailMediaId !== null && !mediaIds.has(exercise.thumbnailMediaId)) {
          missingReferences.push({
            exerciseId: exercise.id,
            missingMediaId: exercise.thumbnailMediaId,
            role: 'thumbnail',
          });
        }
      }

      return {
        ownerlessMediaDeleted: ownerless.map((row) => ({ id: row.id, exerciseId: row.exerciseId })),
        missingReferences,
      };
    },
  };
}

export const mediaRepository = createMediaRepository();
